import { chain } from 'ramda';
import moment from 'moment';

import { getProductFamilyPath } from 'utils/isGroupBooking';
import { getPendingPaymentURL, getRefundMethodsURL } from 'utils/urls';
import { bookingStatus } from 'types/booking';
import { getResarailBookingInfos } from './ExternalReferencesPopup/ExternalReferencesPopup.utils';

import { BUSY_STATES } from './BookingDetailsTable.constants';
import { codes, statusShortCode } from '../../constants/payment';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';

import { PAO_BOOKING_REFERENCE_SOURCE, BENERAIL_BOOKING_REFERENCE_SOURCE } from './ExternalReferencesPopup/ExternalReferencesPopup';

const { CONFIRMED, PENDING_REFUND, ON_HOLD, PROVISIONAL, TEMPORARILY_ON_HOLD } = bookingStatus;

export const getConfirmedNonEurostarExternalReferenceProducts = ({ booking = {} }) =>
  [
    [...(booking.outbound || [])]
      .filter(
        ({ cancelled, serviceType, products = [] }) =>
          !cancelled &&
          !EUROSTAR_CARRIERS.includes(serviceType?.code) &&
          products.some(({ cancelled, provisional, externalProductReference }) => !cancelled && !provisional && !!externalProductReference)
      )
      ?.flatMap(({ products = [] }) => products.map(({ externalProductReference }) => externalProductReference)),
    [...(booking.inbound || [])]
      .filter(
        ({ cancelled, serviceType, products = [] }) =>
          !cancelled &&
          !EUROSTAR_CARRIERS.includes(serviceType?.code) &&
          products.some(({ cancelled, provisional, externalProductReference }) => !cancelled && !provisional && !!externalProductReference)
      )
      ?.flatMap(({ products = [] }) => products.map(({ externalProductReference }) => externalProductReference))
  ]?.flat();

export const filterExternalReferencesByExternalReferenceProducts = ({
  externalBookingReferences = [],
  confirmedNonEurostarExternalReferenceProducts = []
}) =>
  externalBookingReferences.filter(({ externalId, source }) => {
    if ([PAO_BOOKING_REFERENCE_SOURCE, BENERAIL_BOOKING_REFERENCE_SOURCE].includes(source)) {
      return confirmedNonEurostarExternalReferenceProducts.includes(externalId);
    }

    return [{ externalId, source }];
  });

export const getExternalReferencesCount = ({ booking = {} }) => {
  const confirmedNonEurostarExternalReferenceProducts = getConfirmedNonEurostarExternalReferenceProducts({ booking });
  const filteredExternalReferencesByExternalReferenceProducts = filterExternalReferencesByExternalReferenceProducts({
    externalBookingReferences: booking.externalBookingReferences,
    confirmedNonEurostarExternalReferenceProducts
  });
  const allReferences = [...filteredExternalReferencesByExternalReferenceProducts, ...getResarailBookingInfos({ booking })];
  return allReferences?.filter(({ source }) => source !== 'PAO').length;
};

const getAllProducts = chain((leg) => leg.products.concat(leg.addOnLeg ? leg.addOnLeg.products : []));

export const isBookingOnHold = (displayStatus) => [TEMPORARILY_ON_HOLD, ON_HOLD].includes(displayStatus);

export const shouldButtonBeDisabled = (displayStatus, bookingLevelButtonsAreDisabled) => {
  const isBookingBusy = BUSY_STATES.includes(displayStatus);

  return isBookingBusy || (displayStatus === CONFIRMED && bookingLevelButtonsAreDisabled);
};

export const canCancelAllActiveProducts = (inbound, outbound) => {
  const allOutboundProducts = getAllProducts(outbound);
  const allInboundProducts = getAllProducts(inbound);
  const allProducts = allOutboundProducts.concat(allInboundProducts);

  return allProducts.every((p) => p.cancelled || p.canBeCancelled);
};

export const shouldOverrideRules = ({ inbound, outbound, canOverrideRules }) =>
  !canCancelAllActiveProducts(inbound, outbound) && canOverrideRules;

export const shouldCancelBeDisabled = ({ displayStatus, outbound, inbound, canOverrideRules }) =>
  !(isBookingOnHold(displayStatus) || canCancelAllActiveProducts(inbound, outbound) || canOverrideRules);

export const havePendingSocialMediaPayment = (payments) =>
  payments?.some((payment) => payment.paymentStatus === statusShortCode.pending && payment.method === codes.SOCIAL_MEDIA);

export const havePendingOrFailedSocialMediaPayment = (payments) =>
  payments?.some(
    (payment) => [statusShortCode.failed, statusShortCode.pending].includes(payment.paymentStatus) && payment.method === codes.SOCIAL_MEDIA
  );

export const buildBookingUrl = (args) => {
  const { displayStatus, reference, productFamilyGroup, currency, membershipNumber = null, payments, changeExpiryTimestamp = null } = args;
  if (displayStatus === ON_HOLD || displayStatus === TEMPORARILY_ON_HOLD || havePendingOrFailedSocialMediaPayment(payments)) {
    if (changeExpiryTimestamp) {
      return getPendingPaymentURL(reference, currency, membershipNumber) + '&addPassengers=true';
    }
    return getPendingPaymentURL(reference, currency, membershipNumber);
  }

  if (displayStatus === PENDING_REFUND) {
    return getRefundMethodsURL(reference, currency, membershipNumber);
  }

  if (displayStatus === PROVISIONAL) {
    return `/booking/${reference}/${getProductFamilyPath(productFamilyGroup)}`;
  }

  return '';
};

export const shouldDisableCompleteRefundCta = (payments) => {
  if (!payments) {
    return true;
  }

  const dateOfRefund = payments.find((payment) => payment.method === 'refund');

  if (dateOfRefund) {
    const pendingRefundStateDate = moment(dateOfRefund.transactionTimestamp);
    const minDateCompleteRefund = moment('2020-07-01');

    return pendingRefundStateDate < minDateCompleteRefund;
  }

  return true;
};

/**
 * Format ISO date string to DD MMM YYYY
 * @param {string} date ISO date
 * @param {string} locale current locale
 * @returns {string} Formatted date
 */
export const formatBookingDate = (date) => {
  const unixDate = Date.parse(date);
  if (Number.isNaN(unixDate)) {
    return '';
  }

  return moment(unixDate).format('DD MMM YYYY');
};

export const isVoidEligible = ({ booking, displayStatus }) => {
  if (displayStatus !== CONFIRMED) return false;
  if (!booking.confirmedTimestamp || !moment().isBefore(moment(booking.confirmedTimestamp).add(24, 'hours'))) return false;

  const outboundLegs = (booking.outbound || []).filter((leg) => !leg.cancelled);
  const inboundLegs = (booking.inbound || []).filter((leg) => !leg.cancelled);
  const allLegs = [...outboundLegs, ...inboundLegs];

  if (allLegs.length === 0) return false;
  if (!allLegs.every((leg) => EUROSTAR_CARRIERS.includes(leg.serviceType?.code))) return false;

  const isSameCarrierConnection = (legs) => legs.length > 1 && legs.every((leg) => leg.serviceType?.code === legs[0].serviceType?.code);
  if (isSameCarrierConnection(outboundLegs) || isSameCarrierConnection(inboundLegs)) {
    return false;
  }

  return true;
};
